import { InlineAlert } from '../../components/BaseComponents'
import { useState, useEffect } from "react";
import dayjs from "dayjs";
import { Button, LoadingSpinner } from "../../components/BaseComponents";
import CanonicalModal from "../../components/BaseComponents/CanonicalModal/CanonicalModal";
import Input from "../../components/BaseComponents/Forms/Input";
import OrganisationJurisdictionSelect from "../../components/BaseComponents/OrganisationJurisdictionSelect";
import useApi from "../../hooks/useApi";
import { getApiErrorMessage, getCSRFToken } from "../../lib/utils";

type EditTrainingSessionModalProps = {
    onClose: () => void,
    onEdited: (newId: string) => void,
    isOpen?: boolean,
    sessionId: string,
    sessionName: string,
    sessionStartDate: string | null,
    sessionJurisdictionCode: string,
};

function EditTrainingSessionModal({
    onClose,
    onEdited,
    isOpen,
    sessionId,
    sessionName,
    sessionStartDate,
    sessionJurisdictionCode,
}: EditTrainingSessionModalProps) {
    const [name, setName] = useState(sessionName);
    const [startDate, setStartDate] = useState(sessionStartDate ? dayjs(sessionStartDate).format('YYYY-MM-DD') : '');
    const [jurisdictionCode, setJurisdictionCode] = useState<string | undefined>(sessionJurisdictionCode);
    const api = useApi();

    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')

    const reset = () => {
        setName(sessionName);
        setStartDate(sessionStartDate ? dayjs(sessionStartDate).format('YYYY-MM-DD') : '');
        setJurisdictionCode(sessionJurisdictionCode);
        setError('');
    };

    // Sync prop changes to state
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        reset();
    }, [sessionName, sessionStartDate, sessionJurisdictionCode])

    const edit = async () => {
        setLoading(true)
        setError('')
        try {
            const result = await api.put(`/admin/training-sessions/${sessionId}`, {
                name,
                // Send an explicit null (not omit the key) when cleared - the backend distinguishes
                // "this field wasn't sent" (leave the gate as-is) from "this field was cleared"
                // (open the gate) by raw key presence, not by the value.
                startDate: startDate || null,
                jurisdictionCode,
                _csrf: await getCSRFToken(),
            })
            onEdited(result.data.id);
        } catch (err: any) {
            setError(getApiErrorMessage(err, 'Failed to update this training session.'));
        } finally {
            setLoading(false);
        }
    }

    return (
        <CanonicalModal
            open={!!isOpen}
            onClose={() => { reset(); onClose(); }}
            size="small"
            title="Edit training session"
            actions={
                <>
                    <Button onClick={edit} additionalClass="mr-5" disabled={!name} type="Primary" text="Save session" />
                    <Button onClick={onClose} type="Outlined" text="Cancel" />
                </>
            }
        >
            { loading ? (
                <LoadingSpinner />
            ) : (
                <>
                    <Input
                        label="Session name"
                        name="sessionName"
                        additionalClass="mt-8"
                        value={name}
                        onChange={e => setName(e.target.value)}
                        type="text"
                        placeholder="Session name..."
                    />
                    <Input
                        label="Start date (optional - leave blank to start immediately)"
                        name="startDate"
                        additionalClass="mt-8"
                        value={startDate}
                        onChange={e => setStartDate(e.target.value)}
                        type="date"
                    />
                    <OrganisationJurisdictionSelect
                        value={jurisdictionCode}
                        onChange={setJurisdictionCode}
                        label="Jurisdiction"
                        isClearable={false}
                        fillContainer
                    />
                    { error && (
                        <InlineAlert type='error' className='mt-4'>{ error }
                        </InlineAlert>
                    )}
                </>
            )}
        </CanonicalModal>
    )
}

export default EditTrainingSessionModal;
